
/**
 * Safe analytics wrapper that handles missing analytics infrastructure
 */
export class Analytics {
  private instance: string;
  private utmContent?: string;
  private component?: any;

  constructor(instance: string, utmContent?: string) {
    this.instance = instance;
    this.utmContent = utmContent;

    // Try to get analytics component if available
    try {
      this.component =
        (window as any).odAnalytics?.component?.('pro_availability', {
          instance,
          version: '1.0.0',
        }) || null;
    } catch {
      this.component = null;
    }
  }

  /**
   * Emits a view event
   */
  view(payload: Record<string, unknown>): void {
    try {
      this.component?.view?.(payload);
    } catch {
      // Silent no-op
    }
  }

  /**
   * Emits a generic event
   */
  event(name: string, payload: Record<string, unknown> = {}): void {
    try {
      this.component?.event?.(name, {
        ...payload,
        instance: this.instance,
        utm_content: this.utmContent,
      });
    } catch {
      // Silent no-op
    }
  }

  /**
   * Emits an open event (e.g., modal, link)
   */
  open(payload: Record<string, unknown>): void {
    try {
      this.component?.open?.(payload);
    } catch {
      // Silent no-op
    }
  }

  /**
   * Emits a close event
   */
  close(payload: Record<string, unknown>): void {
    try {
      this.component?.close?.(payload);
    } catch {
      // Silent no-op
    }
  }

  /**
   * Delegates click tracking to analytics system
   */
  delegateClicks(
    root: Element,
    selector: string,
    getPayload: (element: Element) => Record<string, unknown>
  ): void {
    try {
      this.component?.delegateClicks?.(root, selector, getPayload);
    } catch {
      // Fallback: manual delegation
      root.addEventListener('click', (e) => {
        const target = (e.target as Element).closest(selector);
        if (target) {
          const payload = getPayload(target);
          this.event('click', payload);
        }
      });
    }
  }
}
